
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Bell, TrendingDown, Mail, Check } from 'lucide-react';

export function ExitIntentModal({ isOpen, onClose, product }) {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  if (!isOpen) return null;

  const handleDismiss = () => {
    // Remember that user dismissed the modal
    localStorage.setItem('exit-intent-dismissed', 'true');
    onClose();
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email || isLoading) return;

    setIsLoading(true);
    
    // Simulate API call - replace with actual email collection logic
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Store in localStorage to prevent showing again
    localStorage.setItem('price-alert-email-submitted', 'true');
    localStorage.setItem('price-alert-email', email);
    
    setIsSubmitted(true);
    setIsLoading(false);
    
    // Auto close after success
    setTimeout(() => {
      onClose();
    }, 2000);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={handleDismiss}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white dark:bg-slate-800 rounded-2xl p-6 sm:p-8 max-w-md w-full shadow-2xl"
          onClick={(e) => e.stopPropagation()}
          data-analytics-id="exit-intent-modal"
        >
          <div className="flex justify-between items-start mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                <Bell className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Don't Miss Out!
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Track this item now
                </p>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {isSubmitted ? (
            <div className="text-center space-y-4 mb-6">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto">
                <Check className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  You're all set!
                </h4>
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  We'll notify you at <span className="font-medium">{email}</span> when price tracking becomes available.
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-4 mb-6">
                <div className="flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <Mail className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  <div>
                    <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                      Price Tracking Coming Soon
                    </p>
                    <p className="text-xs text-blue-600 dark:text-blue-400">
                      Be first to know when it's ready
                    </p>
                  </div>
                </div>

                <p className="text-gray-600 dark:text-gray-400 text-sm text-center">
                  Enter your email to get notified when price tracking launches. We'll never spam you.
                </p>
              </div>

              <form onSubmit={handleEmailSubmit} className="space-y-3">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                  required
                />
                <button
                  type="submit"
                  disabled={isLoading || !email}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                  data-analytics-id="exit-intent-email-submit"
                >
                  {isLoading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Mail size={18} />
                  )}
                  {isLoading ? 'Submitting...' : 'Notify Me When Ready'}
                </button>
                <button
                  type="button"
                  onClick={handleDismiss}
                  className="w-full text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 py-2 text-sm transition-colors"
                  data-analytics-id="exit-intent-dismiss"
                >
                  Maybe later
                </button>
              </form>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
